import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { Table as TableIcon } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ToolbarMenu } from '../../ui/ToolbarMenu';

/** Toolbar control that inserts a table, sized with a hover grid. `onInserted` lets a parent popup close too. */
export const TableSizePicker = ({ disabled, onInserted }: { disabled: boolean; onInserted?: () => void }) => {
  const { t } = useTranslation('common');

  return (
    <ToolbarMenu icon={TableIcon} caret="none" layout="fit" disabled={disabled} label={t('editor.toolbar.insert-table')}>
      {(close) => (
        <TableForm
          close={() => {
            close();
            onInserted?.();
          }}
        />
      )}
    </ToolbarMenu>
  );
};

const MAX_TABLE_SIZE = 20;
const MIN_GRID_SIZE = 5;

/** Shows one spare row/column past the selection, so the grid grows as you near its edge. */
const visibleSize = (selected: number) => Math.min(MAX_TABLE_SIZE, Math.max(MIN_GRID_SIZE, selected + 1));

const clampSize = (value: number) => Math.min(MAX_TABLE_SIZE, Math.max(1, value));

/** Froala-style size picker: hover (or arrow keys) to pick rows × columns, click (or Enter) to insert. */
const TableForm = ({ close }: { close: () => void }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const [size, setSize] = useState({ rows: 1, columns: 1 });

  const visibleRows = visibleSize(size.rows);
  const visibleColumns = visibleSize(size.columns);

  const insert = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      rows: String(size.rows),
      columns: String(size.columns),
      // Header row only; Froala tables have no header column
      includeHeaders: { rows: true, columns: false },
    });
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    if (e.key === 'Enter') {
      e.preventDefault();
      insert();
    } else if (step[e.key]) {
      e.preventDefault();
      const [dr, dc] = step[e.key];
      setSize(({ rows, columns }) => ({ rows: clampSize(rows + dr), columns: clampSize(columns + dc) }));
    }
  };

  // Start-aligned: growing only appends cells, so the ones under the pointer never move
  return (
    <div className="flex flex-col items-start gap-1.5 p-2">
      <div
        role="grid"
        tabIndex={0}
        aria-label={t('editor.toolbar.insert-table')}
        aria-describedby="lexical-table-size"
        className="focus-visible:outline-accent-1-100 grid gap-0.5 rounded outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{ gridTemplateColumns: `repeat(${visibleColumns}, 1rem)` }}
        onKeyDown={onKeyDown}
      >
        {Array.from({ length: visibleRows * visibleColumns }, (_, index) => {
          const row = Math.floor(index / visibleColumns) + 1;
          const column = (index % visibleColumns) + 1;
          const selected = row <= size.rows && column <= size.columns;
          return (
            <div
              // Keyed by position: index keys would reuse cells across rows when a column is added
              key={`${row}:${column}`}
              aria-hidden
              className={`size-4 cursor-pointer rounded-sm border ${
                selected ? 'border-accent-1-100 bg-accent-1-35' : 'border-greyscale-15 bg-white'
              }`}
              // Real pointer movement only: Chrome re-fires hover after re-renders, which would undo arrow keys
              onPointerMove={() =>
                setSize((current) => (current.rows === row && current.columns === column ? current : { rows: row, columns: column }))
              }
              onClick={insert}
            />
          );
        })}
      </div>
      <span id="lexical-table-size" className="text-pbk-12 text-text-2 tabular-nums" aria-live="polite">
        {t('editor.toolbar.table-size', { rows: size.rows, columns: size.columns })}
      </span>
    </div>
  );
};
